import { AxiosAdapter, AxiosRequestConfig, AxiosResponse } from 'axios'
import Cookie from 'js-cookie'
import { DateTime } from 'luxon'
import type { TGoal, TGoalPatch, TGoalTaskHandling } from '../components/goals/goalTypes'
import { AUTHORIZATION_COOKE, DEFAULT_FOLDER_ID, DONE_FOLDER_ID, TRASH_FOLDER_ID } from '../constants'
import { isDevelopmentMode } from '../environment'
import { TViewMoveAnchor, moveViewToAnchor } from '../utils/overview'
import type {
    TEmailDeliveryStatus,
    TEvent,
    TFeatureFlagKey,
    TFeatureFlags,
    TInternalFeatureFlags,
    TInternalUsageWindow,
    TOverviewView,
    TRecurringTaskTemplate,
    TRoutine,
    TSetting,
    TSupportedView,
    TTaskFolder,
    TTaskResearch,
    TTaskV4,
    TUserInfo,
} from '../utils/types'
import { createGoalReviewFake } from './goalReviewFake'
import {
    GOAL_ID,
    GOAL_TASK_ID,
    RECURRING_TEMPLATE_ID,
    SECOND_GOAL_ID,
    SECOND_RECURRING_TEMPLATE_ID,
    calendars,
    folders,
    goal,
    goalRedraftPaths,
    goalRedraftRevisedPaths,
    goalTask,
    internalDashboardSummary,
    internalEmailDelivery as internalEmailDeliveryFixture,
    internalEmailTests,
    internalFeatureFlags as internalFeatureFlagFixture,
    internalUsage,
    overviewViews,
    recurringTemplate,
    routines as routineFixtures,
    routineOverviewView,
    secondGoal,
    secondRecurringTemplate,
    settings,
    subtasks,
    task,
    taskWithSubtasks,
    userInfo,
} from './walkthroughData'

const WALKTHROUGH_QUERY_PARAM = 'walkthrough'
const WALKTHROUGH_STORAGE_KEY = 'generalTask.walkthroughMode'
const WALKTHROUGH_AUTH_TOKEN = 'walkthrough-token'
// `?walkthrough=nogoals` seeds an account with no goals, the one state the seeded fixture can't
// show. The variant is stored rather than kept in memory so it survives a reload mid-recording.
const WALKTHROUGH_NO_GOALS_MODE = 'nogoals'
// `?walkthrough=trial` and `?walkthrough=readonly` swap the subscribed user for one mid-trial or
// with an expired, unpaid trial (the read-only app).
const WALKTHROUGH_TRIAL_MODE = 'trial'
const WALKTHROUGH_READ_ONLY_MODE = 'readonly'
const WALKTHROUGH_FLAGS_OFF_MODE = 'flags-off'
const WALKTHROUGH_FLAGS_ON_MODE = 'flags-on'
const WALKTHROUGH_DUE_TODAY_MODE = 'due-today'
const WALKTHROUGH_PREVIEW_MODE = 'preview'
const WALKTHROUGH_MODES = [
    'true',
    WALKTHROUGH_NO_GOALS_MODE,
    WALKTHROUGH_TRIAL_MODE,
    WALKTHROUGH_READ_ONLY_MODE,
    WALKTHROUGH_FLAGS_OFF_MODE,
    WALKTHROUGH_FLAGS_ON_MODE,
    WALKTHROUGH_DUE_TODAY_MODE,
    WALKTHROUGH_PREVIEW_MODE,
]

type WalkthroughHandler = (config: AxiosRequestConfig) => unknown
interface WalkthroughRequestData {
    body?: string
    due_date?: string
    id_ordering?: number
    id_task_section?: string
    is_completed?: boolean
    is_deleted?: boolean
    feedback?: string
    question?: string
    recurrence_rate?: number
    day_to_create_task?: number
    month_to_create_task?: number
    parent_task_id?: string
    task?: {
        priority_normalized?: number
        recurring_task_template_id?: string
    }
    title?: string
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value))

const getRequestData = (config: AxiosRequestConfig): WalkthroughRequestData => {
    if (!config.data) return {}
    if (typeof config.data === 'string') return JSON.parse(config.data)
    return config.data
}

let tasks: TTaskV4[] = []
let taskFolders: TTaskFolder[] = []
let views: TOverviewView[] = []
let settingValues: TSetting[] = []
let createdTaskCount = 0
let createdGoalCount = 0
let goals: TGoal[] = []
let goalLinks: Record<string, string> = {}
let recurringTemplates: TRecurringTaskTemplate[] = []
let recurringTemplateLinks: Record<string, string[]> = {}
const goalReview = createGoalReviewFake()
let routines: TRoutine[] = []
let createdRoutineCount = 0
let createdViewCount = 0
let events: TEvent[] = []
let researchStarts: Record<string, number> = {}
let researchPolls: Record<string, number> = {}
let internalFeatureFlags: TInternalFeatureFlags = clone(internalFeatureFlagFixture)
let internalEmailDelivery: TEmailDeliveryStatus = clone(internalEmailDeliveryFixture)
let featureFlagOptIns: Partial<Record<TFeatureFlagKey, boolean>> = {}

const isBrowser = () => typeof window !== 'undefined'

const storedWalkthroughMode = () => (isBrowser() ? window.localStorage.getItem(WALKTHROUGH_STORAGE_KEY) : null)

let walkthroughMode = storedWalkthroughMode() ?? 'true'
let seedsGoals = walkthroughMode !== WALKTHROUGH_NO_GOALS_MODE
let walkthroughUserInfo: TUserInfo = clone(userInfo)

const FEATURE_FLAG_KEYS: TFeatureFlagKey[] = [
    'routines',
    'goals',
    'research',
    'recurrence_suggestions',
    'due_today_email',
    'calendar_planning',
]

// Same rule as the backend: on globally, on for the internal dashboard user, or opted in.
const effectiveFeatureFlags = (): TFeatureFlags => {
    const isInternalUser = walkthroughUserInfo.email.toLowerCase() === 'john@johnglomerate.com'
    const flags = FEATURE_FLAG_KEYS.map((key) => {
        const flag = internalFeatureFlags[key]
        const optedIn = Boolean(featureFlagOptIns[key])
        return {
            key,
            name: flag.name,
            description: flag.description,
            enabled: flag.enabled || isInternalUser || optedIn,
            globally_enabled: flag.enabled,
            opted_in: optedIn,
        }
    })
    return {
        routines: flags[0].enabled,
        goals: flags[1].enabled,
        research: flags[2].enabled,
        recurrence_suggestions: flags[3].enabled,
        due_today_email: flags[4].enabled,
        calendar_planning: flags[5].enabled,
        flags,
    }
}

const resetWalkthroughState = () => {
    tasks = [clone(taskWithSubtasks), ...clone(subtasks), clone(goalTask)]
    taskFolders = clone(folders)
    views = clone(overviewViews)
    settingValues = clone(settings)
    createdTaskCount = 0
    createdGoalCount = 0
    goalReview.reset()
    goals = seedsGoals ? [clone(goal), clone(secondGoal)] : []
    goalLinks = seedsGoals ? { [GOAL_TASK_ID]: GOAL_ID } : {}
    recurringTemplates = [clone(recurringTemplate), clone(secondRecurringTemplate)]
    recurringTemplateLinks = {
        [RECURRING_TEMPLATE_ID]: seedsGoals ? [GOAL_ID, SECOND_GOAL_ID] : [],
        [SECOND_RECURRING_TEMPLATE_ID]: seedsGoals ? [GOAL_ID, SECOND_GOAL_ID] : [],
    }
    routines = clone(routineFixtures)
    createdRoutineCount = 0
    createdViewCount = 0
    events = []
    researchStarts = {}
    researchPolls = {}
    internalFeatureFlags = clone(internalFeatureFlagFixture)
    featureFlagOptIns = {}
    internalEmailDelivery = clone(internalEmailDeliveryFixture)
    // flags-off turns every flag off globally, so the Experimental settings section lists them all;
    // flags-on turns every flag on.
    if ([WALKTHROUGH_FLAGS_OFF_MODE, WALKTHROUGH_FLAGS_ON_MODE].includes(walkthroughMode)) {
        for (const key of FEATURE_FLAG_KEYS) {
            internalFeatureFlags[key] = {
                ...internalFeatureFlags[key],
                enabled: walkthroughMode === WALKTHROUGH_FLAGS_ON_MODE,
                updated_at: new Date().toISOString(),
            }
        }
    }
    if (walkthroughMode === WALKTHROUGH_FLAGS_OFF_MODE) {
        const now = new Date().toISOString()
        tasks[0].research = {
            id: 'walkthrough-saved-research',
            question: 'What was already learned?',
            status: 'completed',
            answer: 'Saved research stays readable while new research is off.',
            sources: [{ title: 'Saved cited source', url: 'https://example.com/saved-research' }],
            created_at: now,
            updated_at: now,
            completed_at: now,
        }
    }
    walkthroughUserInfo = clone(userInfo)
    if ([WALKTHROUGH_FLAGS_OFF_MODE, WALKTHROUGH_FLAGS_ON_MODE].includes(walkthroughMode)) {
        walkthroughUserInfo = {
            ...walkthroughUserInfo,
            name: 'Walkthrough user',
            email: 'walkthrough@example.com',
        }
    }
    // Employee-only preview mode includes multi-select task actions used by focused motion checks.
    if (walkthroughMode === WALKTHROUGH_PREVIEW_MODE) walkthroughUserInfo.is_employee = true
}

resetWalkthroughState()

const listEvents: WalkthroughHandler = (config) => {
    const start = DateTime.fromISO(String(config.params?.datetime_start))
    const end = DateTime.fromISO(String(config.params?.datetime_end))
    return clone(
        events.filter(
            (event) => DateTime.fromISO(event.datetime_end) >= start && DateTime.fromISO(event.datetime_start) <= end
        )
    )
}

// Creating an event waits on Google before responding; the delay keeps that round trip visible so the
// calendar placement can be seen not waiting for it.
export const WALKTHROUGH_EVENT_CREATE_LATENCY_MS = 1500
let createdEventCount = 0
const createEvent: WalkthroughHandler = async (config) => {
    const payload = JSON.parse(config.data || '{}')
    await new Promise((resolve) => setTimeout(resolve, WALKTHROUGH_EVENT_CREATE_LATENCY_MS))
    createdEventCount += 1
    const id = `5f9b3b3b3b3b3b3b3b3e${String(createdEventCount).padStart(4, '0')}`
    events.push({
        id,
        title: payload.summary ?? '',
        body: payload.description ?? '',
        account_id: payload.account_id,
        calendar_id: payload.calendar_id ?? '',
        color_id: '',
        logo: 'gcal',
        deeplink: '',
        datetime_start: DateTime.fromISO(payload.datetime_start).toISO({ suppressMilliseconds: true }),
        datetime_end: DateTime.fromISO(payload.datetime_end).toISO({ suppressMilliseconds: true }),
        can_modify: true,
        conference_call: { url: '', logo: '', platform: '' },
        linked_task_id: payload.task_id ?? '',
        linked_view_id: payload.view_id ?? '',
    })
    return { id }
}

const calendarPlanPreview: WalkthroughHandler = (config) => {
    const payload = getRequestData(config) as unknown as {
        date: string
        timezone: string
        start_time: string
        end_time: string
        account_id: string
        calendar_id: string
    }
    const zone = payload.timezone || DateTime.local().zoneName
    const day = payload.date || DateTime.local().toISODate()
    const start = DateTime.fromISO(`${day}T09:30`, { zone })
    const second = start.plus({ hours: 1 })
    return {
        fingerprint: `walkthrough-${day}`,
        date: day,
        timezone: zone,
        start: DateTime.fromISO(`${day}T${payload.start_time || '09:00'}`, { zone }).toISO(),
        end: DateTime.fromISO(`${day}T${payload.end_time || '17:30'}`, { zone }).toISO(),
        actions: [
            {
                id: '5f9b3b3b3b3b3b3b3b3b3c01',
                type: 'add',
                task_id: task.id,
                title: 'Review the walkthrough-ready task',
                start: start.toISO(),
                end: start.plus({ minutes: 30 }).toISO(),
                reasons: ['Urgent priority', 'Due today'],
                score: { priority: 1, deadline: 0.9, goal: 0, total: 0.63 },
            },
            {
                id: '5f9b3b3b3b3b3b3b3b3b3c02',
                type: 'add',
                task_id: GOAL_TASK_ID,
                title: 'Easy 5k loop',
                start: second.toISO(),
                end: second.plus({ minutes: 45 }).toISO(),
                reasons: ['Run three times a week is behind'],
                score: { priority: 0, deadline: 0, goal: 0.67, total: 0.22 },
            },
        ],
        unscheduled: [
            {
                task_id: taskWithSubtasks.id,
                title: 'Draft the follow-up',
                reasons: ['Next in your task list'],
                score: { priority: 0, deadline: 0, goal: 0, total: 0 },
            },
        ],
    }
}

const confirmCalendarPlan: WalkthroughHandler = (config) => {
    const request = getRequestData(config) as unknown as { request: Record<string, string> }
    const plan = calendarPlanPreview({ ...config, data: JSON.stringify(request.request) }) as {
        actions: Array<Record<string, string>>
    }
    for (const action of plan.actions) {
        events.push({
            id: action.id,
            title: action.title,
            body: '',
            account_id: request.request.account_id,
            calendar_id: request.request.calendar_id,
            color_id: '',
            logo: 'generaltask',
            deeplink: '',
            datetime_start: action.start,
            datetime_end: action.end,
            can_modify: true,
            conference_call: { url: '', logo: '', platform: '' },
            linked_task_id: action.task_id,
            linked_view_id: '',
        })
    }
    return { status: 'applied', applied: plan.actions.length }
}

const getUserInfo: WalkthroughHandler = () => {
    if (walkthroughMode !== WALKTHROUGH_TRIAL_MODE && walkthroughMode !== WALKTHROUGH_READ_ONLY_MODE) {
        return clone(walkthroughUserInfo)
    }
    const isInTrial = walkthroughMode === WALKTHROUGH_TRIAL_MODE
    return {
        ...clone(walkthroughUserInfo),
        subscription_status: '',
        is_subscribed: false,
        is_in_trial: isInTrial,
        trial_days_remaining: isInTrial ? 3 : 0,
        trial_ends_at: DateTime.utc()
            .plus({ days: isInTrial ? 3 : -2 })
            .toISO(),
        has_product_access: isInTrial,
        is_read_only: !isInTrial,
    }
}

const planningContext: WalkthroughHandler = () => ({
    calendar_status: 'ready',
    calendar_issues: [],
    commitments: [
        {
            id: RECURRING_TEMPLATE_ID,
            source: 'recurring',
            title: recurringTemplate.title,
            minutes_per_week: 45,
            occurrences_per_week: 1,
            recurrence_rate: recurringTemplate.recurrence_rate,
            block_duration_minutes: recurringTemplate.block_duration_minutes,
            moves_goal: true,
            reused: false,
            confirmed: true,
        },
        {
            id: 'walkthrough-calendar-focus',
            source: 'calendar',
            title: 'Team planning',
            minutes_per_week: 60,
            occurrences_per_week: 1,
            moves_goal: false,
            reused: false,
            confirmed: true,
        },
    ],
})

const updateSettings: WalkthroughHandler = (config) => {
    for (const [key, value] of Object.entries(getRequestData(config) as unknown as Record<string, string>)) {
        const setting = settingValues.find((setting) => setting.field_key === key)
        if (setting) setting.field_value = value
    }
    return {}
}

const createTask: WalkthroughHandler = (config) => {
    createdTaskCount += 1
    const body = getRequestData(config)
    const taskId = `walkthrough-created-task-${createdTaskCount}`
    const folderId = body.id_task_section
    const newTask: TTaskV4 = {
        ...clone(task),
        id: taskId,
        id_ordering: 0.5 - createdTaskCount / 100,
        title: body.title ?? '',
        body: body.body ?? '',
        due_date: body.due_date ?? '',
        priority_normalized: 0,
        id_folder: folderId,
        id_parent: body.parent_task_id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        optimisticId: undefined,
    }

    tasks.unshift(newTask)

    const folder = taskFolders.find((folder) => folder.id === folderId)
    if (folder) folder.task_ids.unshift(taskId)

    views.forEach((view) => {
        if (view.task_section_id === folderId || (view.type === 'due_today' && body.due_date && !body.parent_task_id))
            view.view_item_ids.unshift(taskId)
    })

    return { task_id: taskId }
}

const modifyTask: WalkthroughHandler = (config) => {
    const url = new URL(config.url || '', config.baseURL || window.location.origin)
    const taskId = url.pathname.split('/tasks/modify/')[1]?.split('/')[0]
    const body = getRequestData(config)
    const updatedTask = tasks.find((task) => task.id === taskId)

    if (updatedTask) {
        const previousFolderId = updatedTask.id_folder
        const nextFolderId =
            body.id_task_section ??
            (body.is_completed === true ? DONE_FOLDER_ID : undefined) ??
            (body.is_deleted === true ? TRASH_FOLDER_ID : undefined)

        updatedTask.title = body.title ?? updatedTask.title
        updatedTask.body = body.body ?? updatedTask.body
        updatedTask.due_date = body.due_date ?? updatedTask.due_date
        updatedTask.id_ordering = body.id_ordering ?? updatedTask.id_ordering
        updatedTask.id_folder = nextFolderId ?? updatedTask.id_folder
        updatedTask.is_done = body.is_completed ?? updatedTask.is_done
        if (body.task?.recurring_task_template_id) {
            if (updatedTask.recurrence_suggestion) updatedTask.recurrence_suggestion.status = 'accepted'
        }
        updatedTask.is_deleted = body.is_deleted ?? updatedTask.is_deleted
        updatedTask.priority_normalized = body.task?.priority_normalized ?? updatedTask.priority_normalized
        updatedTask.recurring_task_template_id =
            body.task?.recurring_task_template_id ?? updatedTask.recurring_task_template_id
        updatedTask.updated_at = new Date().toISOString()

        const dueTodayView = views.find((view) => view.type === 'due_today')
        if (dueTodayView) {
            const dueDate = DateTime.fromISO(updatedTask.due_date)
            const isDue =
                !updatedTask.is_done &&
                !updatedTask.is_deleted &&
                !updatedTask.id_parent &&
                dueDate.isValid &&
                dueDate.year > 1971 &&
                dueDate.startOf('day') <= DateTime.local().endOf('day')
            dueTodayView.view_item_ids = dueTodayView.view_item_ids.filter((id) => id !== updatedTask.id)
            if (isDue) dueTodayView.view_item_ids.push(updatedTask.id)
            dueTodayView.view_item_ids.sort((a, b) => {
                const aTask = tasks.find((candidate) => candidate.id === a)
                const bTask = tasks.find((candidate) => candidate.id === b)
                return (
                    DateTime.fromISO(aTask?.due_date ?? '').toMillis() -
                    DateTime.fromISO(bTask?.due_date ?? '').toMillis()
                )
            })
            dueTodayView.view_items = dueTodayView.view_item_ids.flatMap(
                (id) => tasks.find((candidate) => candidate.id === id) ?? []
            )
        }

        if (nextFolderId && nextFolderId !== previousFolderId) {
            const previousFolder = taskFolders.find((folder) => folder.id === previousFolderId)
            const nextFolder = taskFolders.find((folder) => folder.id === nextFolderId)
            if (previousFolder) previousFolder.task_ids = previousFolder.task_ids.filter((id) => id !== taskId)
            if (nextFolder && taskId && !nextFolder.task_ids.includes(taskId)) nextFolder.task_ids.unshift(taskId)

            const previousView = views.find((view) => view.task_section_id === previousFolderId)
            const nextView = views.find((view) => view.task_section_id === nextFolderId)
            if (previousView) previousView.view_item_ids = previousView.view_item_ids.filter((id) => id !== taskId)
            if (nextView && taskId && !nextView.view_item_ids.includes(taskId)) nextView.view_item_ids.unshift(taskId)
        }
    }

    return updatedTask ?? {}
}

const evaluateRecurrenceSuggestion: WalkthroughHandler = (config) => {
    const taskId = idFromPath(config, '/tasks/recurrence_suggestion/')
    return clone(tasks.find((candidate) => candidate.id === taskId)?.recurrence_suggestion ?? { status: 'ineligible' })
}

const dismissRecurrenceSuggestion: WalkthroughHandler = (config) => {
    const taskId = idFromPath(config, '/tasks/recurrence_suggestion/')
    const selected = tasks.find((candidate) => candidate.id === taskId)
    if (selected?.recurrence_suggestion) selected.recurrence_suggestion.status = 'dismissed'
    return clone(selected?.recurrence_suggestion ?? { status: 'dismissed' })
}

const createRecurringTemplate: WalkthroughHandler = (config) => {
    const body = getRequestData(config)
    const templateId = `walkthrough-created-recurring-${recurringTemplates.length + 1}`
    recurringTemplates.unshift({
        id: templateId,
        title: body.title ?? '',
        body: body.body ?? '',
        id_task_section: body.id_task_section ?? DEFAULT_FOLDER_ID,
        recurrence_rate: (body.recurrence_rate ?? 2) as TRecurringTaskTemplate['recurrence_rate'],
        day_to_create_task: body.day_to_create_task,
        month_to_create_task: body.month_to_create_task,
        time_of_day_seconds_to_create_task: 0,
        last_backfill_datetime: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_enabled: true,
        is_deleted: false,
    })
    return { template_id: templateId }
}

const startTaskResearch: WalkthroughHandler = (config) => {
    const taskId = idFromPath(config, '/tasks/research/')
    const updatedTask = tasks.find((candidate) => candidate.id === taskId)
    if (!updatedTask) return null
    const starts = (researchStarts[taskId] ?? 0) + 1
    researchStarts[taskId] = starts
    researchPolls[taskId] = 0
    const now = new Date().toISOString()
    const question = getRequestData(config).question ?? ''
    const retained = retainedResearchResult(updatedTask.research)
    updatedTask.research = {
        id: `walkthrough-research-${taskId}-${starts}`,
        question,
        status: starts === 1 ? 'failed' : 'queued',
        error_code: starts === 1 ? 'provider' : undefined,
        created_at: now,
        updated_at: now,
        retained,
    }
    return clone(updatedTask.research)
}

const getTaskResearch: WalkthroughHandler = (config) => {
    const taskId = idFromPath(config, '/tasks/research/')
    const updatedTask = tasks.find((candidate) => candidate.id === taskId)
    if (!updatedTask?.research) return null
    if (['queued', 'in_progress'].includes(updatedTask.research.status)) {
        const polls = (researchPolls[taskId] ?? 0) + 1
        researchPolls[taskId] = polls
        updatedTask.research =
            polls < 2
                ? { ...updatedTask.research, status: 'in_progress', updated_at: new Date().toISOString() }
                : walkthroughResearchResult(updatedTask.research)
    }
    return clone(updatedTask.research)
}

const retainedResearchResult = (research?: TTaskResearch): TTaskResearch['retained'] => {
    if (!research) return undefined
    if (research.status === 'completed' && research.answer && research.sources && research.completed_at) {
        return {
            question: research.question,
            answer: research.answer,
            sources: clone(research.sources),
            completed_at: research.completed_at,
        }
    }
    return research.retained ? clone(research.retained) : undefined
}

const walkthroughResearchResult = (research: TTaskResearch): TTaskResearch => {
    const result: TTaskResearch = {
        ...research,
        status: 'completed',
        answer: 'Start with the decision criteria, then compare current options against cost, fit, and the time needed to act.',
        sources: [
            { title: 'A practical guide to evidence-based decisions', url: 'https://hbr.org/topic/decision-making' },
            { title: 'Evaluating information sources', url: 'https://guides.library.cornell.edu/evaluate_news' },
        ],
        updated_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
    }
    // Match the durable service: the old snapshot exists only while its replacement is active.
    delete result.retained
    return result
}

const idFromPath = (config: AxiosRequestConfig, prefix: string) => {
    const url = new URL(config.url || '', config.baseURL || window.location.origin)
    return url.pathname.slice(prefix.length).split('/')[0]
}

const isThisWeek = (at?: string) => !!at && new Date(at) >= new Date(DateTime.local().startOf('week').toISO())

/**
 * A stand-in for the server's goal sync (backend/api/goal_progress.go) — just enough for a
 * recording: done linked tasks become Task contributions and this week's count follows.
 */
const syncWalkthroughGoals = () => {
    for (const g of goals) {
        // Mirrors the server: an archived goal's history was discarded and it accrues nothing until
        // it is restored, so nothing here re-derives contributions for it. An achieved goal's final
        // total is held the same way.
        if (g.archivedAt || g.achievedAt) continue
        g.recent = g.recent.filter((c) => {
            const linked = c.taskId ? tasks.find((t) => t.id === c.taskId) : undefined
            if (!linked) return true
            if (linked.is_deleted || !linked.is_done) return false
            if (c.source === 'Recurring') {
                return (
                    !!linked.recurring_task_template_id &&
                    recurringTemplateLinks[linked.recurring_task_template_id]?.includes(g.id)
                )
            }
            return goalLinks[linked.id] === g.id
        })
        for (const t of tasks) {
            if (goalLinks[t.id] !== g.id || t.is_deleted || !t.is_done || g.recent.some((c) => c.taskId === t.id))
                continue
            g.recent.unshift({
                id: `task-${t.id}`,
                title: t.title,
                source: 'Task',
                taskId: t.id,
                at: t.updated_at,
                amount: 1,
            })
        }
        for (const t of tasks) {
            const templateId = t.recurring_task_template_id
            if (
                !templateId ||
                !recurringTemplateLinks[templateId]?.includes(g.id) ||
                t.is_deleted ||
                !t.is_done ||
                g.recent.some((c) => c.taskId === t.id)
            ) {
                continue
            }
            g.recent.unshift({
                id: `task-${t.id}`,
                title: t.title,
                source: 'Recurring',
                taskId: t.id,
                at: t.updated_at,
                amount: 1,
            })
        }
        if (!g.weeklyTarget) continue
        // Same two outputs the server derives together, from one pass, so the meter's glyphs and
        // its count cannot disagree here either. Oldest first: the meter fills left to right.
        const counted = g.recent.filter((c) => isThisWeek(c.at) && (c.amount ?? 0) > 0)
        g.thisWeek = counted.reduce((sum, c) => sum + (c.amount ?? 0), 0)
        g.thisWeekUnits = [...counted].reverse()
        g.weekLabel = `${g.thisWeek} of ${g.weeklyTarget} this week`
    }
}

const listGoals: WalkthroughHandler = () => {
    syncWalkthroughGoals()
    goalReview.attach(goals)
    return clone(goals)
}

const saveGoalReviewEntry: WalkthroughHandler = (config) => {
    const goalId = idFromPath(config, '/goals/reviews/current/goals/')
    const answer = getRequestData(config) as unknown as Parameters<typeof goalReview.save>[1]
    return goalReview.save(goalId, answer, tasks.find((t) => t.id === answer.stepTaskId)?.title)
}

const modifyGoal: WalkthroughHandler = (config) => {
    const g = goals.find((g) => g.id === idFromPath(config, '/goals/'))
    if (!g) return {}
    const patch = getRequestData(config) as unknown as TGoalPatch
    if (patch.title) g.title = patch.title
    if (patch.why != null) g.why = patch.why
    if (patch.timeframeLabel != null) g.timeframeLabel = patch.timeframeLabel
    if (patch.targetLabel != null) g.targetLabel = patch.targetLabel
    if (patch.paceLabel != null) g.paceLabel = patch.paceLabel
    if (patch.progressMode != null) g.progressMode = patch.progressMode
    if (patch.goalType != null) g.goalType = patch.goalType
    if (patch.phases != null) g.phases = patch.phases
    if (patch.planCadences != null) g.planCadences = patch.planCadences
    if (patch.status) g.status = patch.status
    if (patch.startDate != null) g.startDate = patch.startDate
    if (patch.weeklyTarget != null) g.weeklyTarget = patch.weeklyTarget
    if (patch.rampStepUpDisabled != null) g.rampStepUpDisabled = patch.rampStepUpDisabled
    if (patch.unit != null) {
        if (patch.unit) g.unit = patch.unit
        else delete g.unit
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'endDate')) g.endDate = patch.endDate
    if (g.status === 'off_track' || g.status === 'behind') g.status = 'on_track'
    syncWalkthroughGoals()
    return clone(g)
}

const logGoalProgress: WalkthroughHandler = (config) => {
    const g = goals.find((g) => g.id === idFromPath(config, '/goals/'))
    if (!g) return {}
    const log = getRequestData(config) as unknown as { id: string; title: string; amount: number }
    const id = `log-${log.id}`
    if (!g.recent.some((c) => c.id === id)) {
        g.recent.unshift({ id, title: log.title, source: 'Logged', at: new Date().toISOString(), amount: log.amount })
    }
    syncWalkthroughGoals()
    return clone(g)
}

/**
 * The archive, matching backend/api/goal_archive.go: the goal's tasks are handled the way the caller
 * chose, its recurring hooks go either way, its contributions are discarded, and archived_at lands
 * last. Restoring brings back the goal and whichever links survived — never the history.
 */
const archiveGoal: WalkthroughHandler = (config) => {
    const goalId = idFromPath(config, '/goals/')
    const g = goals.find((g) => g.id === goalId)
    if (!g) return {}
    const { task_handling: taskHandling, reason } = getRequestData(config) as unknown as {
        task_handling: TGoalTaskHandling
        reason?: 'done'
    }
    const linkedTaskIds = Object.keys(goalLinks).filter((taskId) => goalLinks[taskId] === goalId)
    if (taskHandling === 'archive') {
        for (const taskId of linkedTaskIds) {
            const linked = tasks.find((t) => t.id === taskId)
            if (linked) linked.is_deleted = true
        }
    }
    // The links go either way, so a task that comes back later comes back unfiled.
    for (const taskId of linkedTaskIds) delete goalLinks[taskId]
    for (const [templateId, goalIds] of Object.entries(recurringTemplateLinks)) {
        recurringTemplateLinks[templateId] = goalIds.filter((id) => id !== goalId)
    }
    g.recent = []
    g.progress = 0
    g.progressLabel = 'Progress not kept'
    g.thisWeek = 0
    g.thisWeekUnits = []
    g.weekLabel = ''
    g.contractLine = ''
    g.archivedAt = new Date().toISOString()
    delete g.achievedAt
    if (reason) g.archivedReason = reason
    else delete g.archivedReason
    return clone(g)
}

const unarchiveGoal: WalkthroughHandler = (config) => {
    const g = goals.find((g) => g.id === idFromPath(config, '/goals/'))
    if (!g) return {}
    delete g.archivedAt
    delete g.archivedReason
    syncWalkthroughGoals()
    return clone(g)
}

/**
 * Achieving, matching backend/api/goal_achieve.go: completed tasks are credited first and keep their
 * links, open tasks are unlinked (and removed if asked), hooks are dropped, achieved_at lands last.
 */
const achieveGoal: WalkthroughHandler = (config) => {
    const goalId = idFromPath(config, '/goals/')
    const g = goals.find((g) => g.id === goalId)
    if (!g) return {}
    if (g.achievedAt) return clone(g)
    const { task_handling: taskHandling } = getRequestData(config) as unknown as { task_handling: TGoalTaskHandling }
    syncWalkthroughGoals()
    const openTaskIds = Object.keys(goalLinks).filter(
        (taskId) => goalLinks[taskId] === goalId && !tasks.find((t) => t.id === taskId)?.is_done
    )
    for (const taskId of openTaskIds) {
        const linked = tasks.find((t) => t.id === taskId)
        if (linked && taskHandling === 'archive') linked.is_deleted = true
        delete goalLinks[taskId]
    }
    for (const [templateId, goalIds] of Object.entries(recurringTemplateLinks)) {
        recurringTemplateLinks[templateId] = goalIds.filter((id) => id !== goalId)
    }
    g.achievedAt = new Date().toISOString()
    return clone(g)
}

const reopenGoal: WalkthroughHandler = (config) => {
    const g = goals.find((g) => g.id === idFromPath(config, '/goals/'))
    if (!g) return {}
    delete g.achievedAt
    syncWalkthroughGoals()
    return clone(g)
}

const linkTaskToGoal: WalkthroughHandler = (config) => {
    const taskId = idFromPath(config, '/goals/task_links/')
    const { goal_id: goalId } = getRequestData(config) as unknown as { goal_id: string | null }
    if (goalId) goalLinks[taskId] = goalId
    else delete goalLinks[taskId]
    return {}
}

// Feedback gets a revised plan, so the round trip is demonstrable without an OpenAI key.
const draftGoalPaths: WalkthroughHandler = (config) => {
    const { feedback } = getRequestData(config)
    return {
        status: 'ready',
        paths: clone(feedback?.trim() ? goalRedraftRevisedPaths : goalRedraftPaths),
    }
}

const createGoal: WalkthroughHandler = (config) => {
    createdGoalCount += 1
    const body = getRequestData(config) as unknown as TGoal
    const saved: TGoal = { ...clone(body), id: body.id || `walkthrough-created-goal-${createdGoalCount}`, recent: [] }
    goals.unshift(saved)
    syncWalkthroughGoals()
    return clone(saved)
}

const linkRecurringTemplateToGoals: WalkthroughHandler = (config) => {
    const templateId = idFromPath(config, '/goals/recurring_template_links/')
    const { goal_ids: goalIds } = getRequestData(config) as unknown as { goal_ids: string[] }
    recurringTemplateLinks[templateId] = goalIds
    return {}
}

// Hold time is the one editor that writes a template, so the walkthrough has to accept the same
// PATCH the real handler does — otherwise the section renders but cannot be exercised offline.
const modifyRecurringTemplate: WalkthroughHandler = (config) => {
    const templateId = idFromPath(config, '/recurring_task_templates/modify/')
    const template = recurringTemplates.find((candidate) => candidate.id === templateId)
    if (!template) return {}
    const patch = getRequestData(config) as unknown as Partial<TRecurringTaskTemplate>
    if (patch.title != null) template.title = patch.title
    if (patch.recurrence_rate != null) template.recurrence_rate = patch.recurrence_rate
    if (patch.time_of_day_seconds_to_create_task != null) {
        template.time_of_day_seconds_to_create_task = patch.time_of_day_seconds_to_create_task
    }
    if (patch.block_duration_minutes != null) template.block_duration_minutes = patch.block_duration_minutes
    if (patch.day_to_create_task != null) template.day_to_create_task = patch.day_to_create_task
    if (patch.is_enabled != null) template.is_enabled = patch.is_enabled
    if (patch.is_deleted != null) template.is_deleted = patch.is_deleted
    return {}
}

const syncRoutineViews = () => {
    for (const view of views.filter((view) => view.type === 'routine')) {
        const routine = routines.find((candidate) => candidate.id === view.routine_id)
        if (!routine) continue
        view.name = routine.name
        view.view_items = clone(routine.items)
        view.view_item_ids = routine.items.map((item) => item.id)
        view.status = routine.status
        view.remaining_count = routine.remaining_count
        view.streak = routine.streak
        view.has_tasks_completed_today = routine.status === 'complete'
    }
}

// Same rule as getOrderedRoutines: Daily Overview card order is the routine order, and routines
// without a card follow in their last order.
const recomputeRoutineProgression = () => {
    let foundCurrent = false
    const cardPositions = new Map<string, number>()
    views.forEach((view) => {
        if (view.type === 'routine' && view.routine_id && !cardPositions.has(view.routine_id)) {
            cardPositions.set(view.routine_id, cardPositions.size)
        }
    })
    const position = (routine: TRoutine) => cardPositions.get(routine.id) ?? Number.MAX_SAFE_INTEGER
    routines.sort((a, b) => position(a) - position(b) || a.id_ordering - b.id_ordering)
    routines.forEach((routine, index) => (routine.id_ordering = index + 1))
    for (const routine of routines) {
        const wasComplete = routine.status === 'complete'
        routine.remaining_count = routine.items.filter((item) => !item.is_completed).length
        if (routine.items.length === 0) routine.status = 'empty'
        else if (routine.remaining_count === 0) routine.status = 'complete'
        else if (!foundCurrent) {
            routine.status = 'current'
            foundCurrent = true
        } else routine.status = 'up_next'
        if (!wasComplete && routine.status === 'complete') routine.streak += 1
        if (wasComplete && routine.status !== 'complete') routine.streak = Math.max(0, routine.streak - 1)
    }
    syncRoutineViews()
}

const completeRoutineItem: WalkthroughHandler = (config) => {
    const itemId = idFromPath(config, '/routine_items/')
    const body = getRequestData(config)
    for (const routine of routines) {
        const item = routine.items.find((candidate) => candidate.id === itemId)
        if (item) item.is_completed = body.is_completed ?? item.is_completed
    }
    recomputeRoutineProgression()
    return {}
}

// Mirrors the server: a routine's Overview card goes after the last routine card (on top when there
// is none), once; an existing card is returned as-is.
const placeRoutineView = (routineId: string) => {
    const existing = views.find((view) => view.type === 'routine' && view.routine_id === routineId)
    if (existing) return existing.id
    const routine = routines.find((candidate) => candidate.id === routineId)
    if (!routine) return ''
    createdViewCount += 1
    const view = routineOverviewView(routine, `walkthrough-view-${createdViewCount}`)
    views.splice(views.map((candidate) => candidate.type).lastIndexOf('routine') + 1, 0, view)
    recomputeRoutineProgression()
    return view.id
}

const routineCreatedResponse = (id: string) => ({
    id,
    item_ids: routines.find((routine) => routine.id === id)?.items.map((item) => item.id) ?? [],
    overview_placement: 'placed',
    overview_view_id: placeRoutineView(id),
})

const addOverviewView: WalkthroughHandler = (config) => {
    const body = getRequestData(config) as unknown as { type: string; routine_id?: string }
    if (body.type !== 'routine' || !body.routine_id) return {}
    return { id: placeRoutineView(body.routine_id) }
}

const deleteOverviewView: WalkthroughHandler = (config) => {
    const id = idFromPath(config, '/overview/views/')
    views = views.filter((view) => view.id !== id)
    recomputeRoutineProgression()
    return {}
}

// Same rule as OverviewViewModify: place the card before or after its anchor card.
const modifyOverviewView: WalkthroughHandler = (config) => {
    const id = idFromPath(config, '/overview/views/')
    const body = getRequestData(config) as unknown as { before_view_id?: string; after_view_id?: string }
    const anchor: TViewMoveAnchor | undefined = body.before_view_id
        ? { beforeViewId: body.before_view_id }
        : body.after_view_id
        ? { afterViewId: body.after_view_id }
        : undefined
    if (anchor) views = moveViewToAnchor(views, id, anchor)
    recomputeRoutineProgression()
    return {}
}

const createRoutine: WalkthroughHandler = (config) => {
    createdRoutineCount += 1
    const body = getRequestData(config) as unknown as { name: string; items?: { title: string }[] }
    const id = `walkthrough-routine-${createdRoutineCount}`
    routines.push({
        id,
        name: body.name,
        id_ordering: routines.length + 1,
        status: 'up_next',
        remaining_count: body.items?.length ?? 0,
        streak: 0,
        items: (body.items ?? []).map((item, index) => ({
            id: `${id}-item-${index + 1}`,
            title: item.title,
            id_ordering: index + 1,
            is_completed: false,
        })),
    })
    recomputeRoutineProgression()
    return routineCreatedResponse(id)
}

const modifyRoutine: WalkthroughHandler = (config) => {
    const id = idFromPath(config, '/routines/')
    const patch = getRequestData(config) as unknown as { name?: string; is_deleted?: boolean }
    const routine = routines.find((candidate) => candidate.id === id)
    if (!routine) return {}
    if (patch.is_deleted) {
        routines = routines.filter((candidate) => candidate.id !== id)
        views = views.filter((view) => view.routine_id !== id)
    } else {
        // Routine order follows Daily Overview; the server refuses id_ordering here.
        if (patch.name) routine.name = patch.name
    }
    recomputeRoutineProgression()
    return {}
}

const duplicateRoutine: WalkthroughHandler = (config) => {
    const id = idFromPath(config, '/routines/')
    const source = routines.find((candidate) => candidate.id === id)
    if (!source) return {}
    createdRoutineCount += 1
    const copy = clone(source)
    copy.id = `walkthrough-routine-${createdRoutineCount}`
    copy.name = `${source.name} copy`
    copy.status = 'up_next'
    copy.streak = 0
    copy.items = copy.items.map((item, index) => ({ ...item, id: `${copy.id}-item-${index + 1}`, is_completed: false }))
    copy.id_ordering = routines.length + 1
    routines.push(copy)
    recomputeRoutineProgression()
    return routineCreatedResponse(copy.id)
}

const createRoutineItem: WalkthroughHandler = (config) => {
    const routineId = idFromPath(config, '/routines/')
    const routine = routines.find((candidate) => candidate.id === routineId)
    if (!routine) return {}
    const body = getRequestData(config)
    const id = `${routine.id}-item-${routine.items.length + 1}`
    // Like the backend, a step added to an already-complete routine counts as done today.
    routine.items.push({
        id,
        title: body.title ?? '',
        id_ordering: routine.items.length + 1,
        is_completed: routine.status === 'complete',
    })
    recomputeRoutineProgression()
    return { id }
}

const modifyRoutineItem: WalkthroughHandler = (config) => {
    const itemId = idFromPath(config, '/routine_items/')
    const patch = getRequestData(config)
    for (const routine of routines) {
        const item = routine.items.find((candidate) => candidate.id === itemId)
        if (!item) continue
        if (patch.is_deleted) routine.items = routine.items.filter((candidate) => candidate.id !== itemId)
        else {
            if (patch.title) item.title = patch.title
            if (patch.id_ordering) {
                routine.items = routine.items.filter((candidate) => candidate.id !== itemId)
                routine.items.splice(Math.max(0, patch.id_ordering - 1), 0, item)
            }
        }
        routine.items.forEach((candidate, index) => (candidate.id_ordering = index + 1))
    }
    recomputeRoutineProgression()
    return {}
}

const supportedOverviewViews = (): TSupportedView[] => [
    {
        type: 'routine',
        name: 'Routines',
        logo: 'generaltask',
        is_nested: true,
        is_linked: true,
        authorization_url: '',
        views: routines.map((routine) => {
            const view = views.find((candidate) => candidate.routine_id === routine.id)
            return {
                view_id: view?.id ?? '',
                name: routine.name,
                is_linked: true,
                task_section_id: '',
                routine_id: routine.id,
                logo: 'generaltask',
                is_added: Boolean(view),
            }
        }),
    },
]

const featureFlagPatch = (config: AxiosRequestConfig) =>
    Object.entries(getRequestData(config) as Record<string, unknown>).filter(
        (entry): entry is [TFeatureFlagKey, boolean] =>
            FEATURE_FLAG_KEYS.includes(entry[0] as TFeatureFlagKey) && typeof entry[1] === 'boolean'
    )

// Same rule as the backend: everyone while a flag is on globally, otherwise opt-ins plus John.
const internalFeatureFlagsResponse = () => {
    const response = clone(internalFeatureFlags)
    for (const flag of Object.values(response)) {
        flag.enabled_user_count = flag.enabled ? internalDashboardSummary.counts.total_users : flag.opted_in_count + 1
    }
    return response
}

const updateInternalFeatureFlags: WalkthroughHandler = (config) => {
    for (const [key, enabled] of featureFlagPatch(config)) {
        internalFeatureFlags[key] = {
            ...internalFeatureFlags[key],
            enabled,
            updated_at: new Date().toISOString(),
        }
    }
    return internalFeatureFlagsResponse()
}

const updateFeatureFlagOptIns: WalkthroughHandler = (config) => {
    for (const [key, optedIn] of featureFlagPatch(config)) {
        if (Boolean(featureFlagOptIns[key]) !== optedIn) {
            internalFeatureFlags[key].opted_in_count += optedIn ? 1 : -1
        }
        if (optedIn) featureFlagOptIns[key] = true
        else delete featureFlagOptIns[key]
    }
    return effectiveFeatureFlags()
}

// Nothing is sent in walkthrough mode, so every requested email reports as sent.
const updateInternalEmailDelivery: WalkthroughHandler = (config) => {
    const { enabled } = getRequestData(config) as { enabled: boolean }
    internalEmailDelivery = {
        ...internalEmailDelivery,
        switched_on: enabled,
        switch_updated_at: new Date().toISOString(),
        enabled: enabled && !internalEmailDelivery.config_issue,
    }
    return clone(internalEmailDelivery)
}

const sendInternalTestEmails: WalkthroughHandler = (config) => {
    const { type } = getRequestData(config) as { type?: string }
    return internalEmailTests
        .filter((emailTest) => type === 'all' || type === emailTest.key)
        .map((emailTest) => ({ key: emailTest.key, sent: true }))
}

const handlers: Record<string, WalkthroughHandler> = {
    'GET /user_info/': getUserInfo,
    'GET /feature_flags/': () => effectiveFeatureFlags(),
    'PATCH /feature_flags/': updateFeatureFlagOptIns,
    'GET /tasks/v4/': () => clone(tasks),
    'GET /sections/v2/': () => clone(taskFolders),
    'GET /overview/views': () => clone(views),
    'GET /overview/views/': () => clone(views),
    'GET /overview/supported_views/': () => supportedOverviewViews(),
    'POST /overview/views/': addOverviewView,
    'PATCH /overview/views/': modifyOverviewView,
    'DELETE /overview/views/': deleteOverviewView,
    'GET /routines/': () => clone(routines),
    'POST /routines/': createRoutine,
    'PATCH /routines/': modifyRoutine,
    'POST /routines/duplicate/': duplicateRoutine,
    'POST /routines/items/': createRoutineItem,
    'PATCH /routine_items/': modifyRoutineItem,
    'PATCH /routine_items/completion/': completeRoutineItem,
    'GET /settings/': () => clone(settingValues),
    'PATCH /settings/': updateSettings,
    'GET /calendars/': () => calendars,
    'GET /goals/': listGoals,
    'POST /goals/': createGoal,
    'GET /goals/task_links/': () => clone(goalLinks),
    'POST /goals/draft_paths/': draftGoalPaths,
    'POST /goals/planning_context/': planningContext,
    'GET /goals/recurring_template_links/': () => clone(recurringTemplateLinks),
    'GET /goals/reviews/current/': () => goalReview.current(),
    'PATCH /goals/reviews/current/': () => goalReview.dismiss(),
    'PATCH /goals/reviews/current/goals/': saveGoalReviewEntry,
    'PATCH /goals/': modifyGoal,
    'POST /goals/recent/': logGoalProgress,
    'POST /goals/archive/': archiveGoal,
    'POST /goals/unarchive/': unarchiveGoal,
    'POST /goals/achieve/': achieveGoal,
    'POST /goals/reopen/': reopenGoal,
    'PATCH /goals/task_links/': linkTaskToGoal,
    'PATCH /goals/recurring_template_links/': linkRecurringTemplateToGoals,
    'GET /meeting_preparation_tasks/': () => [],
    'GET /tasks/fetch/': () => ({}),
    'GET /events/': listEvents,
    'POST /events/create/gcal/': createEvent,
    'POST /calendar_plans/preview/': calendarPlanPreview,
    'POST /calendar_plans/confirm/': confirmCalendarPlan,
    'GET /notes/': () => [],
    'GET /linked_accounts/': () => [],
    'GET /linked_accounts/supported_types/': () => [],
    'GET /recurring_task_templates/v2/': () => clone(recurringTemplates),
    'GET /recurring_task_templates/backfill_tasks/': () => [],
    'PATCH /recurring_task_templates/modify/': modifyRecurringTemplate,
    'POST /recurring_task_templates/create/': createRecurringTemplate,
    'GET /daily_task_completion/': () => [],
    'GET /internal/summary/': () => internalDashboardSummary,
    'GET /internal/feature_flags/': internalFeatureFlagsResponse,
    'GET /internal/usage/': (config) =>
        internalUsage((config.params?.window as TInternalUsageWindow | undefined) ?? '30d'),
    'PATCH /internal/feature_flags/': updateInternalFeatureFlags,
    'GET /internal/email_tests/': () => ({ delivery: clone(internalEmailDelivery), emails: clone(internalEmailTests) }),
    'PATCH /internal/email_delivery/': updateInternalEmailDelivery,
    'POST /internal/email_tests/': sendInternalTestEmails,
    'POST /tasks/create/gt_task/': createTask,
    'PATCH /tasks/modify/': modifyTask,
    'POST /tasks/research/': startTaskResearch,
    'GET /tasks/research/': getTaskResearch,
    'POST /tasks/recurrence_suggestion/evaluate/': evaluateRecurrenceSuggestion,
    'POST /tasks/recurrence_suggestion/dismiss/': dismissRecurrenceSuggestion,
    'POST /log_events/': () => ({}),
}

export const initializeWalkthroughMode = () => {
    if (!isDevelopmentMode || !isBrowser()) return

    const requestedMode = new URLSearchParams(window.location.search).get(WALKTHROUGH_QUERY_PARAM)
    if (!requestedMode) return

    if (['0', 'false', 'off'].includes(requestedMode)) {
        window.localStorage.removeItem(WALKTHROUGH_STORAGE_KEY)
        Cookie.remove(AUTHORIZATION_COOKE)
        return
    }

    walkthroughMode = requestedMode === '1' ? 'true' : requestedMode
    if (!WALKTHROUGH_MODES.includes(walkthroughMode)) walkthroughMode = 'true'
    seedsGoals = walkthroughMode !== WALKTHROUGH_NO_GOALS_MODE
    window.localStorage.setItem(WALKTHROUGH_STORAGE_KEY, walkthroughMode)
    Cookie.set(AUTHORIZATION_COOKE, WALKTHROUGH_AUTH_TOKEN)
    resetWalkthroughState()
}

const isWalkthroughMode = () => isDevelopmentMode && WALKTHROUGH_MODES.includes(storedWalkthroughMode() ?? '')

const responseFor = (config: AxiosRequestConfig, data: unknown): AxiosResponse => ({
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: {},
})

const errorResponseFor = (config: AxiosRequestConfig, data: unknown): AxiosResponse => ({
    ...responseFor(config, data),
    status: 501,
    statusText: 'Not Implemented',
})

// Routes with an id in the path collapse to one handler key.
const routeKey = (method: string, path: string) => {
    if (method === 'PATCH' && path.startsWith('/tasks/modify/')) return 'PATCH /tasks/modify/'
    if (method === 'POST' && path.startsWith('/tasks/research/')) return 'POST /tasks/research/'
    if (method === 'GET' && path.startsWith('/tasks/research/')) return 'GET /tasks/research/'
    if (method === 'POST' && /\/tasks\/recurrence_suggestion\/[^/]+\/evaluate\/$/.test(path))
        return 'POST /tasks/recurrence_suggestion/evaluate/'
    if (method === 'POST' && /\/tasks\/recurrence_suggestion\/[^/]+\/dismiss\/$/.test(path))
        return 'POST /tasks/recurrence_suggestion/dismiss/'
    if (method === 'PATCH' && path.startsWith('/goals/task_links/')) return 'PATCH /goals/task_links/'
    if (method === 'PATCH' && path.startsWith('/recurring_task_templates/modify/'))
        return 'PATCH /recurring_task_templates/modify/'
    if (method === 'PATCH' && path.startsWith('/goals/recurring_template_links/'))
        return 'PATCH /goals/recurring_template_links/'
    if (method === 'PATCH' && path.startsWith('/goals/reviews/current/goals/'))
        return 'PATCH /goals/reviews/current/goals/'
    if (method === 'POST' && /^\/goals\/[^/]+\/recent\/$/.test(path)) return 'POST /goals/recent/'
    if (method === 'POST' && /^\/goals\/[^/]+\/archive\/$/.test(path)) return 'POST /goals/archive/'
    if (method === 'POST' && /^\/goals\/[^/]+\/unarchive\/$/.test(path)) return 'POST /goals/unarchive/'
    if (method === 'POST' && /^\/goals\/[^/]+\/achieve\/$/.test(path)) return 'POST /goals/achieve/'
    if (method === 'POST' && /^\/goals\/[^/]+\/reopen\/$/.test(path)) return 'POST /goals/reopen/'
    if (method === 'PATCH' && /^\/goals\/[^/]+\/$/.test(path)) return 'PATCH /goals/'
    if (method === 'PATCH' && /^\/overview\/views\/[^/]+\/$/.test(path)) return 'PATCH /overview/views/'
    if (method === 'DELETE' && /^\/overview\/views\/[^/]+\/$/.test(path)) return 'DELETE /overview/views/'
    if (method === 'PATCH' && /^\/routines\/[^/]+\/$/.test(path)) return 'PATCH /routines/'
    if (method === 'POST' && /^\/routines\/[^/]+\/duplicate\/$/.test(path)) return 'POST /routines/duplicate/'
    if (method === 'POST' && /^\/routines\/[^/]+\/items\/$/.test(path)) return 'POST /routines/items/'
    if (method === 'PATCH' && /^\/routine_items\/[^/]+\/completion\/$/.test(path))
        return 'PATCH /routine_items/completion/'
    if (method === 'PATCH' && /^\/routine_items\/[^/]+\/$/.test(path)) return 'PATCH /routine_items/'
    return `${method} ${path}`
}

const walkthroughAdapter: AxiosAdapter = async (config) => {
    const method = (config.method || 'get').toUpperCase()
    const url = new URL(config.url || '', config.baseURL || window.location.origin)
    const key = routeKey(method, url.pathname)
    const handler = handlers[key]

    if (!handler) {
        return Promise.reject({
            message: `Walkthrough mode does not mock ${key}`,
            response: errorResponseFor(config, {
                detail: `Walkthrough mode does not mock ${key}`,
            }),
        })
    }

    return responseFor(config, await handler(config))
}

export const applyWalkthroughAdapter = (config: AxiosRequestConfig): AxiosRequestConfig => {
    if (isWalkthroughMode()) {
        config.adapter = walkthroughAdapter
    }

    return config
}
